import { useState, useEffect } from 'react';
import {
  getFullYearRange,
  getMakesForYear,
  getModelsForYearAndMake,
  getEnginesForVehicle,
  getOilSpecs,
  isElectricVehicle,
  getFilterRiskWarning
} from '../utils/vehicleDatabase';
import './VehicleSelector.css';

/**
 * VehicleSelector Component
 * Phase 9 Fix: Category passed from parent, Year shown first
 * Flow: Year -> Make -> Model -> Engine -> Oil Specs
 *
 * @param {function} onVehicleSelect - Callback when vehicle selection is complete
 * @param {string} category - Service category from parent ('standard', 'european', 'truck')
 * @param {object} initialData - Optional initial selection data
 */
function VehicleSelector({ onVehicleSelect, category, initialData }) {
  // Handle null/undefined initialData
  const safeInitialData = initialData || {};

  const [selectedYear, setSelectedYear] = useState(safeInitialData.year || '');
  const [selectedMake, setSelectedMake] = useState(safeInitialData.make || '');
  const [selectedModel, setSelectedModel] = useState(safeInitialData.model || '');
  const [selectedEngine, setSelectedEngine] = useState(safeInitialData.engine || '');

  const [availableYears, setAvailableYears] = useState([]);
  const [availableMakes, setAvailableMakes] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [availableEngines, setAvailableEngines] = useState([]);

  const [oilSpecs, setOilSpecs] = useState(null);
  const [useMetric, setUseMetric] = useState(false); // false = quarts, true = liters
  const [riskWarning, setRiskWarning] = useState(null);

  // Conversion helpers
  const quartsToLiters = (qts) => (qts * 0.946353).toFixed(1);
  const formatCapacity = (qts) => {
    if (!qts) return 'N/A';
    return useMetric ? `${quartsToLiters(qts)} liters` : `${qts} quarts`;
  };

  // Generate years 1998-2026 (static list, always available)
  useEffect(() => {
    setAvailableYears(getFullYearRange());
  }, []);

  // When year changes, load makes and reset downstream selections
  useEffect(() => {
    if (selectedYear) {
      const makes = getMakesForYear(selectedYear, category);
      setAvailableMakes(makes);

      // Reset downstream selections if current make not in new list
      if (!makes.includes(selectedMake)) {
        setSelectedMake('');
        setSelectedModel('');
        setSelectedEngine('');
        setAvailableModels([]);
        setAvailableEngines([]);
        setOilSpecs(null);
      }
    } else {
      setAvailableMakes([]);
      setSelectedMake('');
      setSelectedModel('');
      setSelectedEngine('');
      setAvailableModels([]);
      setAvailableEngines([]);
      setOilSpecs(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, category]);

  // When make changes, load models and reset downstream selections
  useEffect(() => {
    if (selectedYear && selectedMake) {
      const models = getModelsForYearAndMake(selectedYear, selectedMake);
      setAvailableModels(models);

      // Reset downstream selections if current model not in new list
      if (!models.includes(selectedModel)) {
        setSelectedModel('');
        setSelectedEngine('');
        setAvailableEngines([]);
        setOilSpecs(null);
      }
    } else {
      setAvailableModels([]);
      setSelectedModel('');
      setSelectedEngine('');
      setAvailableEngines([]);
      setOilSpecs(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedMake]);

  // When model changes, load engines, check risk, and reset engine selection
  useEffect(() => {
    if (selectedYear && selectedMake && selectedModel) {
      const engines = getEnginesForVehicle(selectedYear, selectedMake, selectedModel);
      setAvailableEngines(engines);

      // Check for high-risk filter model
      const warning = getFilterRiskWarning(selectedYear, selectedMake, selectedModel);
      setRiskWarning(warning.isHighRisk ? warning : null);

      // Reset engine selection if current engine not in new list
      if (!engines.find(e => e.code === selectedEngine)) {
        setSelectedEngine('');
        setOilSpecs(null);
      }
    } else {
      setAvailableEngines([]);
      setSelectedEngine('');
      setOilSpecs(null);
      setRiskWarning(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedMake, selectedModel]);

  // When engine changes, load oil specs and notify parent
  useEffect(() => {
    if (selectedYear && selectedMake && selectedModel && selectedEngine) {
      const vehicleData = getOilSpecs(selectedYear, selectedMake, selectedModel, selectedEngine);
      setOilSpecs(vehicleData);

      // Notify parent component of complete selection
      if (onVehicleSelect && vehicleData) {
        onVehicleSelect({
          category: category,
          year: selectedYear,
          make: selectedMake,
          model: selectedModel,
          engine: selectedEngine,
          specs: vehicleData.specs // Pass nested specs object to parent
        });
      }
    } else {
      setOilSpecs(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedMake, selectedModel, selectedEngine, category, onVehicleSelect]);

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
  };

  const handleMakeChange = (e) => {
    setSelectedMake(e.target.value);
  };

  const handleModelChange = (e) => {
    setSelectedModel(e.target.value);
  };

  const handleEngineChange = (e) => {
    setSelectedEngine(e.target.value);
  };

  return (
    <div className="vehicle-selector">
      <h3>Vehicle Information</h3>

      {/* Year Dropdown - Always visible first */}
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="vehicle-year">Year *</label>
          <select
            id="vehicle-year"
            value={selectedYear}
            onChange={handleYearChange}
            required
          >
            <option value="">Select year...</option>
            {availableYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Make Dropdown */}
      {selectedYear && (
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="vehicle-make">Make *</label>
            <select
              id="vehicle-make"
              value={selectedMake}
              onChange={handleMakeChange}
              required
            >
              <option value="">Select make...</option>
              {availableMakes.map(make => (
                <option key={make} value={make}>{make}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Model Dropdown */}
      {selectedYear && selectedMake && (
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="vehicle-model">Model *</label>
            <select
              id="vehicle-model"
              value={selectedModel}
              onChange={handleModelChange}
              required
            >
              <option value="">Select model...</option>
              {availableModels.map(model => (
                <option key={model} value={model}>{model}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* High-Risk Filter Warning */}
      {riskWarning && (
        <div className="filter-risk-warning">
          <strong>Important:</strong> {riskWarning.reason}
        </div>
      )}

      {/* Engine Dropdown */}
      {selectedYear && selectedMake && selectedModel && availableEngines.length > 0 && (
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="vehicle-engine">Engine *</label>
            <select
              id="vehicle-engine"
              value={selectedEngine}
              onChange={handleEngineChange}
              required
            >
              <option value="">Select engine...</option>
              {availableEngines.map(engine => (
                <option key={engine.code} value={engine.code}>
                  {engine.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Oil Specifications Display Panel */}
      {oilSpecs && (
        <div className="oil-specs-panel">
          <h4>Oil & Service Specifications</h4>

          {isElectricVehicle(selectedEngine) ? (
            <div className="specs-notice electric-vehicle">
              <p><strong>⚡ Electric Vehicle</strong></p>
              <p>This vehicle does not require traditional oil changes.</p>
              {oilSpecs.notes && <p className="notes">{oilSpecs.notes}</p>}
            </div>
          ) : (
            <div className="specs-grid">
              {/* Engine Oil Information */}
              <div className="specs-section">
                <h5>Engine Oil</h5>
                <div className="spec-item">
                  <span className="spec-label">Oil Type:</span>
                  <span className="spec-value">{oilSpecs.specs?.oil_type || 'N/A'}</span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">Capacity:</span>
                  <span className="spec-value">{formatCapacity(oilSpecs.specs?.oil_capacity_qts)}</span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">Drain Plug Torque:</span>
                  <span className="spec-value">
                    {oilSpecs.specs?.drain_plug_torque_ft_lbs ? `${oilSpecs.specs.drain_plug_torque_ft_lbs} ft-lbs` : 'N/A'}
                  </span>
                </div>
              </div>

              {/* Oil Filter Information */}
              <div className="specs-section">
                <h5>Oil Filter</h5>
                <div className="spec-item">
                  <span className="spec-label">OEM:</span>
                  <span className="spec-value">{oilSpecs.specs?.filter_oem || 'N/A'}</span>
                </div>
                {oilSpecs.specs?.filter_wix && (
                  <div className="spec-item">
                    <span className="spec-label">WIX:</span>
                    <span className="spec-value">{oilSpecs.specs.filter_wix}</span>
                  </div>
                )}
                {oilSpecs.specs?.filter_fram && (
                  <div className="spec-item">
                    <span className="spec-label">FRAM:</span>
                    <span className="spec-value">{oilSpecs.specs.filter_fram}</span>
                  </div>
                )}
                {oilSpecs.specs?.filter_kn && (
                  <div className="spec-item">
                    <span className="spec-label">K&N:</span>
                    <span className="spec-value">{oilSpecs.specs.filter_kn}</span>
                  </div>
                )}
              </div>

              {/* Transmission Fluid - Hidden from customer, kept for future use */}
              {/* {(oilSpecs.atf_type || oilSpecs.manual_fluid_type) && (
                <div className="specs-section">
                  <h5>Transmission Fluid</h5>
                  {oilSpecs.atf_type && (
                    <div className="spec-item">
                      <span className="spec-label">ATF Type:</span>
                      <span className="spec-value">{oilSpecs.atf_type}</span>
                    </div>
                  )}
                  {oilSpecs.atf_quantity_qts && (
                    <div className="spec-item">
                      <span className="spec-label">ATF Quantity:</span>
                      <span className="spec-value">{formatCapacity(oilSpecs.atf_quantity_qts)}</span>
                    </div>
                  )}
                  {oilSpecs.manual_fluid_type && (
                    <div className="spec-item">
                      <span className="spec-label">Manual Type:</span>
                      <span className="spec-value">{oilSpecs.manual_fluid_type}</span>
                    </div>
                  )}
                  {oilSpecs.manual_fluid_quantity_qts && (
                    <div className="spec-item">
                      <span className="spec-label">Manual Quantity:</span>
                      <span className="spec-value">{formatCapacity(oilSpecs.manual_fluid_quantity_qts)}</span>
                    </div>
                  )}
                </div>
              )} */}

              {/* Wheel Torque */}
              {oilSpecs.specs?.wheel_torque_ft_lbs && (
                <div className="specs-section">
                  <h5>Wheel Torque</h5>
                  <div className="spec-item">
                    <span className="spec-label">Lug Nut Torque:</span>
                    <span className="spec-value">{oilSpecs.specs.wheel_torque_ft_lbs} ft-lbs</span>
                  </div>
                </div>
              )}

              {/* Unit Converter Button */}
              <div className="specs-section full-width">
                <button
                  type="button"
                  className="unit-toggle-btn"
                  onClick={() => setUseMetric(!useMetric)}
                >
                  {useMetric ? 'Switch to Quarts' : 'Switch to Liters'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default VehicleSelector;
